/**
 * Three.js scene primitives — built with React.createElement (no JSX) so the
 * visual-edits babel plugin does not inject `x-line-number` style attributes
 * into the lowercase R3F intrinsic elements (which break R3F's applyProps).
 */
import { Component, createElement as h, Suspense, useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, Environment, ContactShadows, Float, Html } from "@react-three/drei";
import * as THREE from "three";

const F1_CAR_MODEL_URL = (process.env.REACT_APP_F1_CAR_MODEL_URL || "").trim();

if (F1_CAR_MODEL_URL) {
    useGLTF.preload(F1_CAR_MODEL_URL);
}

class ModelErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        if (process.env.NODE_ENV !== "production") {
            console.warn("Car model failed to load.", error);
        }
    }

    render() {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}

function CarModel({ rotationRef }) {
    const ref = useRef();
    const { scene } = useGLTF(F1_CAR_MODEL_URL);

    const tunedScene = useMemo(() => {
        const cloned = scene.clone(true);
        cloned.traverse((obj) => {
            if (obj.isMesh && obj.material) {
                obj.castShadow = true;
                obj.receiveShadow = true;
                if (obj.material.metalness !== undefined) {
                    obj.material.envMapIntensity = 1.4;
                }
            }
        });
        return cloned;
    }, [scene]);

    useFrame((_, dt) => {
        if (!ref.current) return;
        ref.current.rotation.y = THREE.MathUtils.damp(
            ref.current.rotation.y,
            rotationRef.current,
            4,
            dt
        );
        ref.current.position.y = -0.55 + Math.sin(performance.now() * 0.0008) * 0.02;
    });

    return h(
        "group",
        { ref, position: [0, -0.55, 0], scale: 1 },
        h("primitive", { object: tunedScene })
    );
}

function SweepLight() {
    const ref = useRef();
    useFrame(({ clock }) => {
        if (!ref.current) return;
        const t = clock.getElapsedTime();
        ref.current.position.x = Math.sin(t * 0.4) * 4;
        ref.current.position.z = Math.cos(t * 0.4) * 4;
    });
    return h("pointLight", {
        ref,
        color: "#E10600",
        intensity: 20,
        distance: 9,
        position: [3, 1.5, 2],
    });
}

function SceneContents({ rotationRef, isMobile }) {
    const carLayer = F1_CAR_MODEL_URL
        ? h(
            ModelErrorBoundary,
            null,
            h(
                Suspense,
                {
                    fallback: h(
                        Html,
                        { center: true },
                        h(
                            "div",
                            {
                                className: "font-mono text-xs text-zinc-500 tracking-widest",
                            },
                            "LOADING CAR\u2026"
                        )
                    ),
                },
                h(
                    Float,
                    {
                        speed: 0.6,
                        rotationIntensity: 0.05,
                        floatIntensity: 0.25,
                    },
                    h(CarModel, { rotationRef })
                ),
                h(ContactShadows, {
                    position: [0, -0.95, 0],
                    opacity: 0.55,
                    scale: 9,
                    blur: 2.6,
                    far: 3,
                    color: "#000",
                })
            )
        )
        : null;

    return h(
        "group",
        null,
        h("ambientLight", { intensity: 0.32 }),
        h("directionalLight", {
            position: [5, 8, 5],
            intensity: 1.4,
            castShadow: true,
            "shadow-mapSize-width": 1024,
            "shadow-mapSize-height": 1024,
        }),
        h("directionalLight", {
            position: [-6, 4, -3],
            intensity: 0.6,
            color: "#7BB7FF",
        }),
        h("pointLight", {
            position: [-3, 1.5, 2],
            intensity: 18,
            color: "#E10600",
            distance: 9,
        }),
        !isMobile && h(SweepLight, null),
        carLayer,
        h(Environment, {
            preset: "studio",
            environmentIntensity: 0.45,
        })
    );
}

export default function ThreeScene({ rotationRef }) {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        setIsMobile(window.innerWidth < 768);
    }, []);

    return h(
        Canvas,
        {
            onCreated: ({ gl }) => {
                gl.toneMapping = THREE.ACESFilmicToneMapping;
                gl.toneMappingExposure = 1.15;
            },
            camera: { position: [0, 0.6, 4.6], fov: 32 },
            dpr: isMobile ? [1, 1.25] : [1, 1.8],
            gl: {
                antialias: !isMobile,
                alpha: true,
                powerPreference: "high-performance",
            },
            style: { background: "transparent" },
        },
        h(SceneContents, { rotationRef, isMobile })
    );
}
